import {
    AfterViewInit,
    Component,
    computed,
    ElementRef,
    EventEmitter,
    Inject,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    Signal,
    ViewChild,
} from '@angular/core';
import { NotificationsService } from 'angular2-notifications';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { BsModalService } from 'ngx-bootstrap/modal';
import { Subject, timer } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';

import { ConfirmationModalComponent } from '../../../../shared/modal-windows/confirmation-modal/confirmation-modal.component';
import { ReminderModalComponent } from '../../../../shared/modal-windows/reminder-modal/reminder-modal.component';
import { TicketDetailsComponent } from 'src/app/ticket-details/ticket-details.component';
import { HideReplyModalComponent } from '../../../../shared/modal-windows/hide-reply-modal/hide-reply-modal.component';
import { ShowReplyModalComponent } from '../../../../shared/modal-windows/show-reply-modal/show-reply-modal.component';
import { CommonDataService } from 'src/sb-tools/typescript/services/common-data.service';
import { QuillHelperService } from '@sb-tools/typescript/services/quill/quill-helper.service';
import { RightPanelService } from 'src/app/core/services/right-panel.service';
import { ReplyLikeService } from 'src/app/core/services/ticket/reply-like.service';
import { ReplyDataService } from 'src/app/core/services/ticket/reply-data.service';
import { ReplyService } from 'src/app/core/services/ticket/reply.service';
import { NoteService } from 'src/app/core/services/note.service';
import { ReplyStoreService } from 'src/app/core/services/ticket/reply-store.service';
import { ForwardMessageService } from 'src/app/core/services/forward-message.service';
import { ThreadFilterService } from 'src/app/core/services/thread-filter.service';
import { TicketService } from 'src/app/core/services/ticket/ticket.service';
import { OpenTicketInNewWindowService } from 'src/app/core/services/ticket/open-ticket-in-new-window.service';
import { ReplyUpdateModel } from '@sb-tools/typescript/models/reply-update-model.model';
import { ThreadElementDTO } from '@sb-tools/typescript/models/dto/thread-element.dto';
import { TicketReminderDTO } from '@sb-tools/typescript/models/dto/ticket-reminder.dto';
import { TicketSource } from '@sb-tools/typescript/enums/ticket-source.enum';
import { AttachmentType } from 'src/sb-tools/typescript/enums/attachment-type.enum';
import { TicketStatus } from '@sb-tools/typescript/enums/ticket-status.enum';
import { RightPanelType } from 'src/app/core/enums/right-panel-type.enum';
import { ForwardEntityTypeEnum } from 'src/app/core/enums/forward-entity-type.enum';
import { ReactionTooltipTextType } from 'src/app/core/enums/reaction-tooltip-text-type.enum';
import { TicketThreadSocialMediaComponent } from '../ticket-thread-social-media.component';
import { DetailedTicketDTO } from '../../../../core/models/dto/detailed-ticket.dto';
import { LikeStatus } from '../../../../core/models/like-status.model';
import { getUrlWithQueryParams } from '../../../../core/utils/get-url-with-query-params.util';
import { ReplyReminderAlertComponent } from '../../../../shared/components/reply-reminder-alert/reply-reminder-alert.component';
import { UserAvatarComponent } from '../../../../../sb-modules/Frontend-User-Avatar-Module/user-avatar.component';
import { DatePipe, NgClass } from '@angular/common';
import { NoteLinkLineComponent } from '../../note-link-line/note-link-line.component';
import { HighlightReplyMentionComponent } from '../../../../shared/components/highlight-reply-mention/highlight-reply-mention.component';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { InlineSVGComponent } from '../../../../../sb-modules/Frontend-Inline-SVG-Module/inline-svg.component';
import { EditReplySocialMediaComponent } from '../edit-reply-social-media/edit-reply-social-media.component';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { ClipboardModule } from 'ngx-clipboard';
import { ReminderMenuComponent } from '../../../../shared/components/reminder-menu/reminder-menu.component';
import { FileViewComponent } from '../../../../../sb-modules/Frontend-Document-Viewer-Module/components/file-view/file-view.component';
import { FilePreviewComponent } from '../../../../../sb-modules/Frontend-Document-Viewer-Module/components/file-preview/file-preview.component';
import { ReactionsComponent } from '../reactions/reactions.component';
import { TagsComponent } from '../../../tags/tags.component';
import { MsSentimentComponent } from '../../ms-sentiment/ms-sentiment.component';
import { NewReplySocialMediaComponent } from '../new-reply-social-media/new-reply-social-media.component';
import { TimeAgoPipe } from '../../../../shared/pipes/time-ago.pipe';
import { LastSubReplyPipe } from '../../../../shared/pipes/last-subReply.pipe';
import { EncodeHtmlSymbolsPipe } from '@sb-tools/typescript/pipes/encode-html-symbols.pipe';
import { isInboxElementVisible } from '../../../../core/utils/is-inbox-element-visible.util';

@Component({
    selector: 'app-reply-social-media',
    templateUrl: './reply-social-media.component.html',
    styleUrls: ['./reply-social-media.component.scss'],
    imports: [
        ReplyReminderAlertComponent,
        UserAvatarComponent,
        NgClass,
        NoteLinkLineComponent,
        HighlightReplyMentionComponent,
        TooltipModule,
        InlineSVGComponent,
        EditReplySocialMediaComponent,
        BsDropdownModule,
        TranslateModule,
        ClipboardModule,
        ReminderMenuComponent,
        FileViewComponent,
        FilePreviewComponent,
        ReactionsComponent,
        TagsComponent,
        MsSentimentComponent,
        NewReplySocialMediaComponent,
        DatePipe,
        EncodeHtmlSymbolsPipe,
        TimeAgoPipe,
        LastSubReplyPipe,
    ],
})
export class ReplySocialMediaComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {
    @ViewChild('replyMenuBtn') replyMenuBtn: ElementRef;

    @Input() isSubReply;
    @Input() level;
    @Input() parentReply?;
    @Input() reply: ThreadElementDTO | any;
    @Input() ticket: DetailedTicketDTO;

    @Output() openReactionsWindow = new EventEmitter<{ isTicketReactions: boolean; replyId: number }>();

    attachmentTypeEnum = AttachmentType;
    forwardEntityTypeEnum = ForwardEntityTypeEnum;
    handledByTooltip: string;
    isReplyMenuDropUp: boolean;
    isReminderMenuDropUp: boolean;
    isHandling: boolean;
    isHandlingAvailable: boolean;
    isHidingAvailable: boolean;
    isUpdatingLikeStatus: boolean;
    newReply: ReplyUpdateModel;
    parentReplyForNewReply;
    reactionTooltipTextType = ReactionTooltipTextType;
    replyUrl: string;
    rightPanelTypeEnum = RightPanelType;
    threadFilterService = ThreadFilterService;
    ticketSourceEnum = TicketSource;
    ticketStatusEnum = TicketStatus;
    readonly isInboxElementVisible = isInboxElementVisible;

    isSelectable: Signal<boolean> = computed(() => {
        return (
            !this.reply.isDeleted &&
            this.ticket.selectedRepliesCount() > 0 &&
            (this.ticket.selectedRepliesCount() < this.MAX_SELECT_COUNT || this.reply.isSelected)
        );
    });

    readonly MAX_SELECT_COUNT = 50;

    private unsubscriber = new Subject<void>();

    constructor(
        public rightPanelService: RightPanelService,
        public commonDataService: CommonDataService,
        public noteService: NoteService,
        private replyLikeService: ReplyLikeService,
        private translateService: TranslateService,
        private bsModalService: BsModalService,
        private replyDataService: ReplyDataService,
        private replyService: ReplyService,
        private replyStoreService: ReplyStoreService,
        public forwardMessageService: ForwardMessageService,
        public openTicketInNewWindowService: OpenTicketInNewWindowService,
        private quillHelperService: QuillHelperService,
        private ticketService: TicketService,
        private notificationsService: NotificationsService,
        @Inject(TicketDetailsComponent) public parent: TicketDetailsComponent,
        @Inject(TicketThreadSocialMediaComponent)
        public ticketThreadSocialMediaComponent: TicketThreadSocialMediaComponent,
    ) {}

    ngOnInit() {
        this.isHandlingAvailable =
            this.commonDataService.customerSettings.handleSettings.isEnabled && !this.reply.isByTeam;

        this.isHidingAvailable =
            this.ticket.constraints.isReplyHidingAllowed &&
            (!this.reply.isByTeam || this.ticket.constraints.isTeammateReplyHidingAllowed) &&
            this.ticket.isEditable &&
            !this.reply.isDraft;

        this.isSubReply = !!this.parentReply;
        this.newReply = new ReplyUpdateModel();
        if (this.ticket.constraints.areSubRepliesAllowed) {
            if (this.reply.level + 1 === this.ticket.constraints.maxLevelsCount) {
                this.newReply.socialMediaSourceId = this.parentReply.socialMediaSourceId;
                this.newReply.parentId = this.parentReply.id;
                this.parentReplyForNewReply = this.parentReply;
            } else {
                this.newReply.socialMediaSourceId = this.reply.socialMediaSourceId;
                this.newReply.parentId = this.reply.id;
                this.parentReplyForNewReply = this.reply;
            }

            const tempReply = this.replyStoreService.getTempReply(this.ticket.id, this.parentReplyForNewReply.id);

            if (tempReply) {
                this.newReply.message.text = tempReply.text;
                this.newReply.message.html = tempReply.html;

                this.showNewSubreplyForm();
            }
        }

        if (this.reply.id) {
            this.replyUrl = getUrlWithQueryParams({
                additionalParams: { ticketId: this.ticket.id, replyId: this.reply.id },
                withCurrentParams: false,
            });
        }
    }

    ngOnChanges() {
        this.updateHandledByTooltip();
    }

    ngAfterViewInit() {
        this.replyMenuBtn.nativeElement.addEventListener('mouseenter', () => {
            this.initDropdownsPositioning();
        });

        timer(50).subscribe(() => {
            this.initDropdownsPositioning();
        });
    }

    deleteReply(): void {
        this.parent.deleteReply(this.reply, this.parentReply);
    }

    handle(): void {
        this.isHandling = true;
        this.handledByTooltip = '';
        const isHandled = !this.reply.handledBy;

        this.replyDataService
            .handleReply(this.ticket.id, this.reply.id, isHandled)
            .pipe(
                takeUntil(this.unsubscriber),
                finalize(() => {
                    this.isHandling = false;
                }),
            )
            .subscribe((handledBy) => {
                this.reply.handledBy = handledBy;
                this.updateHandledByTooltip();
            });
    }

    hideReply(isUserBlocked: boolean) {
        this.replyDataService
            .hideReply(this.ticket.id, this.reply.id, this.reply.isHidden, this.isSubReply, isUserBlocked)
            .pipe(takeUntil(this.unsubscriber))
            .subscribe((reply: ThreadElementDTO) => {
                this.replyService.updateReplyData(this.ticket, reply, this.ticket.socialMediaSourceId, false);
            });
    }

    initDropdownsPositioning(): void {
        const [isReplyMenuDropUp, isReminderMenuDropUp] = this.replyService.getReplyMenusDropUp(this.replyMenuBtn);
        this.isReplyMenuDropUp = isReplyMenuDropUp;
        this.isReminderMenuDropUp = isReminderMenuDropUp;
    }

    onRemindClick(minutes: number): void {
        const dateTime = new Date(new Date().getTime() + 60000 * minutes);

        const reminder: TicketReminderDTO = {
            time: dateTime,
            replyId: this.reply.id,
        };

        this.ticketService
            .addReminder(this.ticket.id, reminder)
            .pipe(takeUntil(this.unsubscriber))
            .subscribe((addedReminder: TicketReminderDTO) => {
                this.reply.reminder = addedReminder;
                this.parent.ticketUpdateStamp = new Date().getTime();
                this.notificationsService.success(
                    this.translateService.instant('Success!'),
                    this.translateService.instant('Reminder was successfully added.'),
                );
            });
    }

    onRemoveReminderClick(): void {
        this.ticketService
            .deleteReminder(this.ticket.id, this.reply.id)
            .pipe(takeUntil(this.unsubscriber))
            .subscribe(() => {
                this.reply.reminder = null;
                this.parent.ticketUpdateStamp = new Date().getTime();
                this.notificationsService.success(
                    this.translateService.instant('Success!'),
                    this.translateService.instant('Reminder was successfully removed.'),
                );
            });
    }

    openReminderModal(): void {
        const initialState = {
            ticketId: this.ticket.id,
            replyId: this.reply.id,
            hasReopenOption: false,
            confirmCallback: (reminder: TicketReminderDTO) => {
                this.reply.reminder = reminder;
                this.parent.ticketUpdateStamp = new Date().getTime();
            },
        };

        this.bsModalService.show(ReminderModalComponent, {
            initialState,
            ignoreBackdropClick: true,
        });
    }

    selectReply(event: PointerEvent, reply: ThreadElementDTO, isSelectable = true, isStopPropagation = false): void {
        if (!isSelectable) {
            return;
        }

        if (isStopPropagation) {
            event.stopPropagation();
        }
        if (reply.parentReplyId) {
            const parentReply = this.findSelectedParent(reply);
            if (reply.id !== parentReply.id) {
                this.toggleChildIsSelected(parentReply, false);
            }
        }
        this.toggleChildIsSelected(reply, !reply.isSelected);
    }

    showEditReply(reply) {
        this.quillHelperService.editedReplyId = reply.id;
        reply.editingReply = !reply.editingReply;
        reply.oldMessage = structuredClone(reply.message);
        reply.oldAttachments = structuredClone(reply.attachments);
    }

    showNewSubreplyForm() {
        let reply;

        if (this.level < this.ticket.constraints.maxLevelsCount) {
            reply = this.reply;
        } else {
            reply = this.parentReply;
        }

        reply.isShowNewSubreplyForm = true;

        if (this.parentReply) {
            this.parentReply.newAttachments = [];
        } else {
            this.ticket.newAttachments = [];
        }

        // todo: rewrite, get element with angular view
        setTimeout(() => {
            $('#new-reply-' + reply.id + ' .ql-editor').focus();
        });
    }

    showPrivateReplyForm(reply) {
        this.parent.openNewPrivateReplyForm(reply);
    }

    showLikes() {
        this.reply.showLikes = true;

        this.replyLikeService
            .getLikeStatus(this.ticket.id, this.reply.id, this.parent.socialMediaSourceId)
            .pipe(takeUntil(this.unsubscriber))
            .subscribe((data) => {
                this.reply.likeCount = data.likeCount;
                this.reply.isLiked = data.isLiked;
                this.reply.reactions = data.reactions;
            });
    }

    toggleLike() {
        if (this.isUpdatingLikeStatus) {
            return;
        }

        this.isUpdatingLikeStatus = true;

        this.replyLikeService
            .changeLikeStatus(
                this.ticket.id,
                this.reply.id,
                this.parent.socialMediaSourceId,
                this.ticket.constraints.areReactionsAllowed ? !this.reply.hasReactionFromTeam : !this.reply.isLiked,
            )
            .pipe(
                finalize(() => {
                    this.isUpdatingLikeStatus = false;
                }),
                takeUntil(this.unsubscriber),
            )
            .subscribe((replyLikeStatus: LikeStatus) => {
                if (this.ticket.constraints.areReactionsAllowed) {
                    this.reply.hasReactionFromTeam = !this.reply.hasReactionFromTeam;
                }

                this.reply.reactions = replyLikeStatus.reactions;
                this.reply.isLiked = replyLikeStatus.isLiked;
                this.reply.likeCount = replyLikeStatus.likeCount;
                this.reply.updateReactionsMarker = new Date().getTime();
            });
    }

    openDeleteReplyModal() {
        const initialState = {
            title: this.translateService.instant('Are you sure you want to delete this reply?'),
            confirmText: this.translateService.instant('Delete'),
            declineText: this.translateService.instant('Cancel'),
            confirmCallback: () => {
                return this.deleteReply();
            },
        };

        this.bsModalService.show(ConfirmationModalComponent, {
            initialState,
        });
    }

    openHideReplyModal() {
        const initialState = {
            confirmCallback: (isUserBlocked: boolean) => {
                return this.hideReply(isUserBlocked);
            },
        };

        this.bsModalService.show(HideReplyModalComponent, {
            initialState,
        });
    }

    openShowReplyModal() {
        if (!this.reply.author.blockingInfo.isBlocked) {
            this.hideReply(null);
            return;
        }

        const initialState = {
            confirmCallback: (isUserBlocked: boolean) => {
                return this.hideReply(isUserBlocked);
            },
        };

        this.bsModalService.show(ShowReplyModalComponent, {
            initialState,
        });
    }

    showAllSubReplies(): void {
        this.reply.areCommentsShown = true;
    }

    private findSelectedParent(reply: ThreadElementDTO): ThreadElementDTO {
        const parentReply = this.replyService.findReply(
            this.ticket.messageThread as ThreadElementDTO[],
            reply.parentReplyId,
        );
        if (!parentReply?.isSelected) {
            return reply;
        }
        return parentReply?.parentReplyId ? this.findSelectedParent(parentReply) : parentReply;
    }

    private toggleChildIsSelected(reply: ThreadElementDTO, isSelected: boolean): void {
        if (reply.isSelected !== isSelected && !reply.isDeleted) {
            reply.isSelected = !reply.isSelected;
            if (reply.isSelected) {
                this.ticket.selectedRepliesCount.update((value) => ++value);
            } else {
                this.ticket.selectedRepliesCount.update((value) => --value);
            }
        }
        reply.subReplies.forEach((subReply) => {
            this.toggleChildIsSelected(subReply, isSelected);
        });
    }

    private updateHandledByTooltip() {
        this.handledByTooltip = this.reply.handledBy
            ? this.translateService.instant('Handled by') + ' ' + this.reply.handledBy.name
            : this.translateService.instant('Click to handle');
    }

    ngOnDestroy() {
        this.unsubscriber.next();
        this.unsubscriber.complete();
    }
}
